import { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const money = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN", {
  maximumFractionDigits: 2,
})}`;

export default function MarginManagement() {
  const [products, setProducts] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/margins/products")
      .then(({ data }) => {
        setProducts(data.products || []);
        setDraft(Object.fromEntries((data.products || []).map((product) => [
          product.id,
          String(product.cogs || 0),
        ])));
      })
      .catch(() => {
        setProducts([]);
        toast.error("Could not load margin settings.");
      });
  };

  useEffect(() => {
    load();
  }, []);

  const changedProducts = useMemo(() => {
    if (!products) {
      return [];
    }
    return products.filter((product) => {
      return Number(product.cogs || 0) !== Number(draft[product.id] || 0);
    });
  }, [draft, products]);

  const manageableProducts = useMemo(() => {
    return (products || []).filter((product) => {
      return ["cups", "shakes", "tubs"].includes(product.category);
    });
  }, [products]);

  const save = async () => {
    if (!changedProducts.length) {
      toast.message("No COGS changes to save.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/margins/products", {
        products: changedProducts.map((product) => ({
          id: product.id,
          cogs: Number(draft[product.id] || 0),
        })),
      });
      toast.success("COGS updated.");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update COGS.");
    } finally {
      setSaving(false);
    }
  };

  if (!products) {
    return <div className="mt-10 label-xs text-espresso-light">Loading…</div>;
  }

  return (
    <section className="mt-8" data-testid="margin-management-panel">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="label-xs text-gold-hover">Owner controls</p>
          <h2 className="mt-2 font-serif text-3xl text-espresso">Margin management</h2>
          <p className="mt-2 max-w-2xl text-sm text-espresso-light">
            Update ingredient costs for cups, shakes, and tubs. Mix & Match box costs stay dynamic.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving || !changedProducts.length}
          data-testid="save-cogs-button"
          className="bg-espresso text-ivory hover:bg-espresso-hover"
        >
          <Save size={15} aria-hidden="true" />
          {saving ? "Saving…" : `Save ${changedProducts.length || ""} changes`}
        </Button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="min-w-[780px] w-full text-sm">
          <thead className="bg-ivory-100 text-left label-xs text-espresso-light">
            <tr>
              <th className="px-5 py-4">Product</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Website price</th>
              <th className="px-5 py-4">Total COGS</th>
              <th className="px-5 py-4">Gross margin</th>
              <th className="px-5 py-4">Margin %</th>
            </tr>
          </thead>
          <tbody>
            {manageableProducts.map((product) => {
              const cogs = Number(draft[product.id] || 0);
              const grossMargin = Number(product.price || 0) - cogs;
              const marginPercent = product.price ? grossMargin * 100 / product.price : 0;
              const warn = product.price > 0 && cogs / product.price > 0.4;
              return (
                <tr
                  key={product.id}
                  className="border-t border-line"
                  data-testid={`margin-product-${product.id}`}
                >
                  <td className="px-5 py-4 text-espresso">
                    <div className="font-serif text-lg">{product.name}</div>
                    {warn && (
                      <div
                        className="mt-1 text-xs text-amber-800"
                        data-testid={`margin-warning-${product.id}`}
                      >
                        COGS is above 40% of price
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 capitalize text-espresso-light">{product.category}</td>
                  <td className="px-5 py-4 text-espresso">{money(product.price)}</td>
                  <td className="px-5 py-4">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft[product.id] ?? ""}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        [product.id]: event.target.value,
                      }))}
                      data-testid={`cogs-input-${product.id}`}
                      aria-label={`COGS for ${product.name}`}
                      className="h-9 w-28 bg-ivory"
                    />
                  </td>
                  <td
                    className={`px-5 py-4 ${
                      grossMargin >= 0 ? "text-emerald-800" : "text-rose-700"
                    }`}
                    data-testid={`gross-margin-${product.id}`}
                  >
                    {money(grossMargin)}
                  </td>
                  <td
                    className="px-5 py-4 text-espresso"
                    data-testid={`gross-margin-rate-${product.id}`}
                  >
                    {marginPercent.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}